import type { InvoiceListItem } from '@tandemone/types'
import { Ban, Check, Download, Eye, MoreHorizontal, Send } from 'lucide-react'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { reportError } from '@/domains/observability'
import { navigateTab, openBlankTab } from '@/lib/new-tab'

import { getInvoicePdfUrl } from './api'
import { useCancelInvoice } from './use-cancel-invoice'
import { useMarkInvoicesPaid } from './use-mark-invoices-paid'
import { useSendInvoice } from './use-send-invoice'

/* the tab is opened by the caller, synchronously on the click: ios treats a
 * window.open after the await as a popup and blocks it. */
export async function downloadInvoicePdf(
  id: string,
  tab?: Window | null
): Promise<void> {
  try {
    const { downloadUrl } = await getInvoicePdfUrl(id)
    navigateTab(downloadUrl, tab)
  } catch (error) {
    tab?.close()
    reportError(error)
    toast.error('Failed to generate PDF')
  }
}

export function InvoiceRowActions({ invoice }: { invoice: InvoiceListItem }) {
  const navigate = useNavigate()
  const send = useSendInvoice()
  const markPaid = useMarkInvoicesPaid()
  const cancel = useCancelInvoice()

  /** only the issuer (direction 'sent') can mutate; a recipient just views +
   * downloads what they've been billed.
   */
  const isIssuer = invoice.direction === 'sent'
  const canSend =
    isIssuer && (invoice.status === 'draft' || invoice.status === 'sent')
  const canMarkPaid = isIssuer && invoice.status === 'sent'
  const canCancel =
    isIssuer && invoice.status !== 'paid' && invoice.status !== 'cancelled'

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant='ghost'
          size='icon'
          aria-label={`Actions for ${invoice.invoiceNumber}`}
        >
          <MoreHorizontal className='size-4' />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end' className='w-52'>
        <DropdownMenuItem
          onSelect={() => void navigate(`/invoices/${invoice.id}`)}
        >
          <Eye className='size-3.5' />
          View details
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => void downloadInvoicePdf(invoice.id, openBlankTab())}
        >
          <Download className='size-3.5' />
          Download PDF
        </DropdownMenuItem>
        {canSend && (
          <DropdownMenuItem
            disabled={send.isPending}
            onSelect={event => {
              event.preventDefault()
              send.mutate(invoice.id)
            }}
          >
            <Send className='size-3.5' />
            {invoice.status === 'draft' ? 'Send invoice' : 'Resend invoice'}
          </DropdownMenuItem>
        )}
        {canMarkPaid && (
          <DropdownMenuItem
            disabled={markPaid.isPending}
            onSelect={event => {
              event.preventDefault()
              markPaid.mutate([invoice.id])
            }}
          >
            <Check className='size-3.5' />
            Mark as paid
          </DropdownMenuItem>
        )}
        {canCancel && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              variant='destructive'
              disabled={cancel.isPending}
              onSelect={event => {
                event.preventDefault()
                cancel.mutate(invoice.id)
              }}
            >
              <Ban className='size-3.5' />
              Cancel invoice
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
